/**
 * @file use-dialog-a11y.ts
 *
 * Hook that gives an overlay/slide-in panel non-modal dialog behaviour: it
 * merges the caller's forwarded ref with an internal one, moves focus into the
 * panel when it opens and restores it to the triggering element on close, and
 * invokes `onClose` when Escape is pressed while open. Extracted from
 * {@link SideDrawer} so the panel stays presentational and the behaviour (and
 * its keyboard/focus edge cases) can be tested on its own and reused by future
 * dialogs.
 */

import { type ForwardedRef, useCallback, useEffect, useRef } from 'react';

/**
 * Wires non-modal dialog accessibility onto a panel element.
 *
 * @param isOpen - Whether the panel is currently shown.
 * @param onClose - Dismiss handler invoked on Escape (optional).
 * @param forwardedRef - The ref forwarded by the consuming component, merged
 *   with the hook's internal ref so the caller still gets the node.
 *
 * @returns A callback ref to attach to the panel root.
 */
export function useDialogA11y<T extends HTMLElement>(
  isOpen: boolean,
  onClose: (() => void) | undefined,
  forwardedRef: ForwardedRef<T>,
): (node: T | null) => void {
  const nodeRef = useRef<T | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const setRef = useCallback(
    (node: T | null) => {
      nodeRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef],
  );

  // Move focus into the panel on open; restore it to the trigger on close.
  useEffect(() => {
    if (isOpen) {
      triggerRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      nodeRef.current?.focus();
    } else if (triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [isOpen]);

  // Dismiss on Escape while open.
  useEffect(() => {
    if (!isOpen || !onClose) {
      return;
    }
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  return setRef;
}
